import useSnackbar from "@/helpers/add-snackbar"
import { Button } from "@material-ui/core"
import { AssignmentOutlined } from "@material-ui/icons"
import React from "react"

interface PasteButtonProps {
  buttonText?: string
  color?: "inherit" | "primary" | "secondary"
  variant?: "contained" | "outlined" | "text"
  isSuperUserOnly?: boolean
  textAreaRef?: React.RefObject<HTMLTextAreaElement>
  onPaste?: (text: string) => void
  size?: "small" | "medium" | "large"
  disabled?: boolean
  style?: React.CSSProperties
  className?: string
  onError?: (error: Error) => void
}

const PasteButton: React.FC<PasteButtonProps> = ({
  buttonText = "Paste",
  color = "primary",
  variant = "contained",
  isSuperUserOnly = false,
  textAreaRef,
  onPaste,
  size = "medium",
  disabled = false,
  style = {},
  className = "",
  onError
}) => {
  const { addSnackbar } = useSnackbar()

  const handlePaste = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText()

        if (textAreaRef && textAreaRef.current) {
          textAreaRef.current.value = text
          textAreaRef.current.focus()

          const event = new Event("input", { bubbles: true })
          textAreaRef.current.dispatchEvent(event)
        }

        if (onPaste) {
          onPaste(text)
        }

        addSnackbar(
          "Content pasted: " +
            text.substring(0, 100) +
            (text.length > 100 ? "..." : ""),
          "success"
        )
      } else {
        addSnackbar(
          "Please use Ctrl+V (or Cmd+V on Mac) to paste content",
          "info"
        )

        if (textAreaRef && textAreaRef.current) {
          textAreaRef.current.focus()
        }
      }
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error("Permission denied or clipboard unavailable")

      if (onError) {
        onError(error)
      }
      addSnackbar(`Failed to paste: ${error.message}`, "error")
      if (textAreaRef?.current) {
        textAreaRef.current.focus()
      }
      addSnackbar(
        "Please use Ctrl+V (or Cmd+V on Mac) to paste content manually, or update your clipboard permissions",
        "info"
      )
    }
  }

  const buttonStyle = isSuperUserOnly ? { backgroundColor: "brown" } : {}
  return (
    <Button
      variant={variant}
      color={color}
      onClick={handlePaste}
      size={size}
      disabled={disabled}
      style={{ ...buttonStyle, ...style }}
      className={className}
      startIcon={<AssignmentOutlined />}
    >
      {buttonText}
    </Button>
  )
}

export default PasteButton
